import { z } from 'zod';

// Package Feature
export interface PackageFeature {
  id: string;
  name: string;
  description?: string;
  included: boolean;
  order: number;
}

// Base types for package relationships
export interface PackagePlan {
  id: number;
  name: string;
  monthly_price: number | null;
  display_order: number;
}

export interface PackageCategory {
  id: number;
  name: string;
  display_order: number;
}

export interface PackageChannel {
  id: number;
  name: string;
  source: string;
  source_id: number;
  source_type: 'package_channel' | 'plan' | 'category';
}

// Main Package type
export interface Package {
  id: number;
  provider_id: number;
  version_id: number;
  region_id: number;
  name: string;
  description: string | null;
  monthly_price: number | null;
  yearly_price: number | null;
  package_type: 'standalone' | 'plan_bundle' | 'category_bundle' | 'mixed';
  is_addon: boolean;
  is_featured: boolean;
  display_order: number;
  color_index: number | null;
  features: PackageFeature[];
  marketing_tagline: string | null;
  best_for: string | null;
  created_at: string;
  updated_at: string;
  // Relationships (optional)
  plans?: PackagePlan[];
  categories?: PackageCategory[];
  channels?: PackageChannel[];
}

// Package with full relationship data
export interface PackageWithRelations extends Package {
  plans: PackagePlan[];
  categories: PackageCategory[];
  channels: PackageChannel[];
  channel_count: number;
  plan_count: number;
  category_count: number;
}

// Form schemas
export const packageFeatureSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Feature name is required'),
  description: z.string().optional(),
  included: z.boolean(),
  order: z.number()
});

export const packageSchema = z.object({
  name: z.string().min(1, 'Package name is required').max(255),
  description: z.string().nullable().optional(),
  monthly_price: z.number().nullable().optional(),
  yearly_price: z.number().nullable().optional(),
  package_type: z.enum(['standalone', 'plan_bundle', 'category_bundle', 'mixed']).optional(),
  is_addon: z.boolean().optional(),
  is_featured: z.boolean().optional(),
  display_order: z.number().optional(),
  color_index: z.number().min(1).max(30).nullable().optional(),
  features: z.array(packageFeatureSchema).optional(),
  marketing_tagline: z.string().nullable().optional(),
  best_for: z.string().nullable().optional()
});

export type PackageFormData = z.infer<typeof packageSchema>;

// API params
export interface CreatePackageParams extends PackageFormData {
  provider_id: number;
  version_id: number;
  region_id: number;
  plan_ids?: number[];
  category_ids?: number[];
  channel_ids?: number[];
}

export interface UpdatePackageParams {
  id: number;
  data: Partial<PackageFormData>;
  plan_ids?: number[];
  category_ids?: number[];
  channel_ids?: number[];
}

// Entity type for features (streaming_specifications, promotional_offers, etc.)
export type PackageEntityType = 'package';

export const PACKAGE_ENTITY_TYPE: PackageEntityType = 'package';
